import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { showError } from '../../../../shared/utils/swalHandler';
import {
  getHighlightSegmentsWithTypes,
  hasBlockingRestrictedContent,
} from '../../../../shared/utils/restrictedWordsUtils';
import ChatComposerRestrictedWarning from './ChatComposerRestrictedWarning';

const ChatComposer = ({
  roomId,
  creatorInfo,
  isOnline,
  isRoomInfoReady,
  isChatReady,
  isSending,
  canSendMessages = true,
  sendTextMessage,
  scrollToBottom,
  forceScrollToBottomRef,
  restrictedWords,
  onMessageSent,
  openTipModalRef,
}) => {
  const [messageText, setMessageText] = useState('');
  const messageInputRef = useRef(null);
  const highlightMirrorRef = useRef(null);
  const wrapperRef = useRef(null);
  const roomIdRef = useRef(roomId);
  /** Synchronous guard — Enter / mobile keyboard can fire twice before isSending re-renders. */
  const sendInFlightRef = useRef(false);

  // Expose current composer message, clear callback, and focus for TipModal (message-with-tip flow)
  useEffect(() => {
    if (openTipModalRef?.current && typeof openTipModalRef.current === 'object') {
      openTipModalRef.current.composerMessageForTip = messageText;
      openTipModalRef.current.clearComposerMessage = () => setMessageText('');
      openTipModalRef.current.focusComposer = () => {
        // Only focus (and thus open typepad on phone) when there is message text
        if (messageText.trim().length > 0) {
          messageInputRef.current?.focus?.();
        }
      };
    }
    return () => {
      if (openTipModalRef?.current && typeof openTipModalRef.current === 'object') {
        openTipModalRef.current.focusComposer = null;
      }
    };
  }, [messageText, openTipModalRef]);

  const MIN_INPUT_HEIGHT = 42;
  const MAX_INPUT_HEIGHT = 200;

  const hasBlocking = useMemo(
    () => hasBlockingRestrictedContent(messageText, restrictedWords || []),
    [messageText, restrictedWords]
  );

  const highlightSegments = useMemo(
    () => getHighlightSegmentsWithTypes(messageText, restrictedWords || []),
    [messageText, restrictedWords]
  );

  const hasWatchWarning = useMemo(
    () =>
      messageText.trim().length > 0 &&
      !hasBlocking &&
      highlightSegments.some((s) => s.type === 'watch'),
    [messageText, hasBlocking, highlightSegments]
  );

  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);

  const isComposerDisabled =
    !isRoomInfoReady || !isChatReady || !isOnline || !canSendMessages;
  const canSend =
    isOnline &&
    canSendMessages &&
    !isSending &&
    messageText.trim().length > 0 &&
    !isComposerDisabled &&
    !hasBlocking;

  const handleSendMessage = useCallback(async () => {
    if (sendInFlightRef.current || isSending) return;

    if (!isOnline) {
      showError('Offline', 'No internet connection. Please reconnect to send messages.');
      return;
    }

    const trimmed = messageText.trim();
    if (!trimmed) return;

    if (hasBlocking) {
      showError(
        'Prohibited content',
        'Your message contains prohibited words or links. Please change or remove them before sending.'
      );
      return;
    }

    sendInFlightRef.current = true;
    const sendingRoomId = roomIdRef.current;
    let res;
    try {
      res = await sendTextMessage(trimmed);
    } finally {
      sendInFlightRef.current = false;
    }

    // If the user switched rooms while this send was in-flight,
    // don't show stale errors / mutate the new room's draft.
    if (sendingRoomId !== roomIdRef.current) return;

    if (res?.skipped) return;

    if (res?.success) {
      setMessageText('');
      onMessageSent?.();
      if (forceScrollToBottomRef) {
        forceScrollToBottomRef.current = true;
      }
      // Optimistic append runs before sendTextMessage awaits; the parent's layout effect
      // can run while this ref is still false. Ref updates don't re-render, so scroll here
      // so sending while scrolled up always reveals the new message.
      requestAnimationFrame(() => {
        scrollToBottom?.('smooth');
      });

      // Reset typing area to compact height after send (so it doesn't stay expanded).
      requestAnimationFrame(() => {
        const textarea = messageInputRef.current;
        const wrapper = wrapperRef.current;
        if (textarea && wrapper) {
          textarea.style.height = `${MIN_INPUT_HEIGHT}px`;
          wrapper.style.height = `${MIN_INPUT_HEIGHT}px`;
        }

        // Keep the cursor in the input after sending (once it becomes enabled).
        window.requestAnimationFrame(() => {
          messageInputRef.current?.focus?.();
        });
      });
      return;
    }
    showError('Error', res?.error || 'Failed to send message');
  }, [
    canSendMessages,
    isOnline,
    isSending,
    messageText,
    hasBlocking,
    sendTextMessage,
    scrollToBottom,
    forceScrollToBottomRef,
    onMessageSent,
  ]);

  const handleDraftChange = useCallback((e) => {
    setMessageText(e.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent?.isComposing) return;
      e.preventDefault();
      handleSendMessage();
    },
    [handleSendMessage]
  );

  const handleOverlayScroll = useCallback(() => {
    const ta = messageInputRef.current;
    const mirror = highlightMirrorRef.current;
    if (ta && mirror) mirror.scrollTop = ta.scrollTop;
  }, []);

  // Reset draft input when switching rooms.
  useEffect(() => {
    setMessageText('');
  }, [roomId]);

  // Auto-resize textarea and sync wrapper/highlight height; cap height and scroll (iMessage-style) once it grows tall.
  useEffect(() => {
    const textarea = messageInputRef.current;
    const wrapper = wrapperRef.current;
    if (!textarea || !wrapper) return;

    textarea.style.height = '0px';
    const newHeight = Math.min(textarea.scrollHeight, MAX_INPUT_HEIGHT);
    textarea.style.height = `${Math.max(newHeight, MIN_INPUT_HEIGHT)}px`;
    wrapper.style.height = `${Math.max(newHeight, MIN_INPUT_HEIGHT)}px`;
  }, [messageText]);

  const placeholder = isOnline ? 'Type a message...' : "You're offline";

  return (
    <>
      {!canSendMessages &&
        <ChatComposerRestrictedWarning
          creatorInfo={creatorInfo}
        />
      }
      {hasBlocking && messageText.trim().length > 0 && (
        <div id="restricted-words-warning" className="chat_composer_restricted_warning" role="alert" aria-live="polite">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span>This message contains prohibited words or links and cannot be sent. Please remove them before sending.</span>
        </div>
      )}
      <div className="chat_editer_row">
        <div
          ref={wrapperRef}
          className={`chat_composer_input_wrapper ${hasBlocking ? 'chat_composer_input_restricted' : ''}${hasWatchWarning ? ' chat_composer_input_watch' : ''
            }`}
        >
          <div ref={highlightMirrorRef} className="chat_composer_highlight_container" aria-hidden="true">
            <div className="chat_composer_highlight">
              {!messageText ? (
                <span className="chat_composer_placeholder">{placeholder}</span>
              ) : (
                highlightSegments.map((seg, i) => (
                  <span
                    key={i}
                    className={
                      seg.type === 'watch'
                        ? 'restricted-word-watch'
                        : seg.type === 'prohibit'
                          ? 'restricted-word'
                          : undefined
                    }
                  >
                    {seg.text}
                  </span>
                ))
              )}
            </div>
          </div>
          <textarea
            ref={messageInputRef}
            className="chat_composer_input_overlay"
            value={messageText}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            onScroll={handleOverlayScroll}
            disabled={isComposerDisabled}
            autoComplete="off"
            aria-label="Message"
            aria-invalid={hasBlocking}
            aria-describedby={
              [hasBlocking && 'restricted-words-warning', hasWatchWarning && 'restricted-words-watch-warning']
                .filter(Boolean)
                .join(' ') || undefined
            }
            rows={1}
            placeholder={placeholder}
          />
        </div>
        <button
          type="button"
          disabled={!canSend}
          aria-label={hasBlocking ? "Send message (disabled: contains prohibited words or links)" : "Send message"}
          className={hasBlocking ? "chat_composer_send_disabled" : ""}
          onClick={handleSendMessage}
          onMouseDown={(e) => e.preventDefault()}
          onPointerDown={(e) => e.preventDefault()}
        >
          <i className="fa-solid fa-paper-plane"></i>
        </button>
      </div>
    </>
  );
};

export default React.memo(ChatComposer);

